import { useEffect, useState } from 'react';
import { Modal, Button } from '@symphenia/ui-components';
import { fieldLabel, fieldWrapper, inputBase, selectBase } from '@/lib/form-styles';
import { leseZahl } from '@/lib/speichern';
import { createWorkSchedule, fetchCollectiveAgreements, fetchWorkSchedules } from '../api/payroll.api';
import { todayIso } from '../lib/dates';
import type { CollectiveAgreement, EmploymentType, PayrollEmployee, WorkSchedule } from '../types/payroll.types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  users: PayrollEmployee[];
  /** Nach dem Anlegen öffnet die Seite den Datensatz (AP-36 Fall 2). */
  onAngelegt: (schedule: WorkSchedule) => void;
}

/**
 * Wer schon ein Modell hat, bekommt hier keins mehr (AP-22 Teil 7): Ein Wechsel entsteht auf der
 * Detailseite des Modells. Wieder angeboten wird, wessen jüngstes Modell einen Austritt trägt.
 */
async function mitModell(): Promise<Set<string>> {
  const juengstes = new Map<string, WorkSchedule>();
  for (let page = 1; ; page++) {
    const res = await fetchWorkSchedules({ page, limit: 200 });
    for (const w of res.data) {
      const da = juengstes.get(w.userId);
      if (!da || w.validFrom > da.validFrom) juengstes.set(w.userId, w);
    }
    if (page >= res.meta.totalPages) break;
  }
  return new Set([...juengstes.values()].filter((w) => !w.exitDate).map((w) => w.userId));
}

function userLabel(u: PayrollEmployee): string {
  const name = [u.profile?.firstName, u.profile?.lastName].filter(Boolean).join(' ');
  return name || u.email || '—';
}

/**
 * Arbeitszeitmodell anlegen (AP-36 Fall 2).
 *
 * Nur, was zum Anlegen nötig ist oder danach gesperrt bleibt: Mitarbeiter (nicht
 * mehr änderbar), Gültig-ab, Beschäftigung, Wochenstunden. Das Tages-Soll belegt
 * der Server gleichmäßig auf Mo–Fr vor; Zyklus, Ein- und Austritt und das
 * genaue Soll pflegt man danach auf der Detailseite — dort ohne Knopf.
 */
export function WorkScheduleCreateDialog({ isOpen, onClose, users, onAngelegt }: Props) {
  const [userId, setUserId] = useState('');
  const [validFrom, setValidFrom] = useState(todayIso());
  const [employment, setEmployment] = useState<EmploymentType>('FULL_TIME');
  const [weeklyHours, setWeeklyHours] = useState('38,5');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // AP-22 Teil 3: nur angeboten, wenn der Betrieb Entlohnungsarten führt.
  const [arten, setArten] = useState<CollectiveAgreement[]>([]);
  const [artId, setArtId] = useState('');
  // null = noch nicht geladen; dann bleibt die Auswahl leer statt alle anzubieten.
  const [vergeben, setVergeben] = useState<Set<string> | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setVergeben(null);
    mitModell()
      .then(setVergeben)
      .catch(() => setError('Die vorhandenen Modelle konnten nicht geladen werden.'));
    fetchCollectiveAgreements()
      .then((liste) => setArten(liste.filter((a) => a.isActive)))
      .catch(() => setArten([]));
    setArtId('');
    setUserId('');
    setValidFrom(todayIso());
    setEmployment('FULL_TIME');
    setWeeklyHours('38,5');
    setError(null);
  }, [isOpen]);

  const frei = vergeben ? users.filter((u) => !vergeben.has(u.id)) : [];

  const anlegen = async () => {
    const stunden = leseZahl(weeklyHours);
    if (!userId) return setError('Bitte einen Mitarbeiter wählen.');
    if (!validFrom) return setError('Bitte ein Gültig-ab-Datum angeben.');
    if (stunden === null || stunden < 0 || stunden > 80)
      return setError('Wochenstunden zwischen 0 und 80 angeben.');

    setSaving(true);
    setError(null);
    try {
      const angelegt = await createWorkSchedule({
        userId,
        validFrom,
        employment,
        weeklyHours: stunden,
        ...(artId ? { collectiveAgreementId: artId } : {}),
      });
      onAngelegt(angelegt);
    } catch (err) {
      const antwort = (err as { response?: { data?: { message?: string; code?: string } } })?.response?.data;
      setError(
        antwort?.message === 'payroll.workSchedules.duplicateValidFrom'
          ? 'Für diesen Mitarbeiter gibt es bereits ein Modell mit diesem Gültig-ab.'
          : antwort?.code === 'PAYROLL_COMPENSATION_CHANGE_MID_MONTH'
            ? 'Die Entlohnungsart kann nur zu einem Monatsersten wechseln — wähl als Gültig-ab den Ersten eines Monats.'
            : 'Anlegen fehlgeschlagen.',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Arbeitszeitmodell anlegen"
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button onClick={() => void anlegen()} disabled={saving}>
            {saving ? 'Anlegen…' : 'Anlegen'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>
        )}

        <div className={fieldWrapper}>
          <label className={fieldLabel} htmlFor="ws-new-user">
            Mitarbeiter
          </label>
          <select
            id="ws-new-user"
            className={selectBase}
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
          >
            <option value="">{vergeben ? '— wählen —' : 'Laden…'}</option>
            {frei.map((u) => (
              <option key={u.id} value={u.id}>
                {userLabel(u)}
              </option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground">
            {vergeben && frei.length === 0
              ? 'Alle Mitarbeiter haben ein Modell. Einen Wechsel legst du im Modell des Mitarbeiters an.'
              : 'Nur Mitarbeiter ohne Modell. Einen Wechsel legst du im Modell des Mitarbeiters an.'}
          </p>
        </div>

        <div className={fieldWrapper}>
          <label className={fieldLabel} htmlFor="ws-new-validfrom">
            Gültig ab
          </label>
          <input
            id="ws-new-validfrom"
            type="date"
            className={inputBase}
            value={validFrom}
            onChange={(e) => setValidFrom(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className={fieldWrapper}>
            <label className={fieldLabel} htmlFor="ws-new-employment">
              Beschäftigung
            </label>
            <select
              id="ws-new-employment"
              className={selectBase}
              value={employment}
              onChange={(e) => setEmployment(e.target.value as EmploymentType)}
            >
              <option value="FULL_TIME">Vollzeit</option>
              <option value="PART_TIME">Teilzeit</option>
            </select>
          </div>
          <div className={fieldWrapper}>
            <label className={fieldLabel} htmlFor="ws-new-hours">
              Wochenstunden
            </label>
            <input
              id="ws-new-hours"
              inputMode="decimal"
              className={`${inputBase} text-right tabular-nums`}
              value={weeklyHours}
              onChange={(e) => setWeeklyHours(e.target.value)}
            />
          </div>
        </div>

        {arten.length > 0 && (
          <div className={fieldWrapper}>
            <label className={fieldLabel} htmlFor="ws-new-art">
              Entlohnungsart
            </label>
            <select id="ws-new-art" className={selectBase} value={artId} onChange={(e) => setArtId(e.target.value)}>
              <option value="">Standard des Betriebs</option>
              {arten.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">Ein Wechsel der Entlohnungsart gilt ab einem Monatsersten.</p>
          </div>
        )}

        <p className="text-xs text-muted-foreground">
          Das Tages-Soll wird gleichmäßig auf Montag bis Freitag verteilt. Anpassen kannst du es
          danach im Modell.
        </p>
      </div>
    </Modal>
  );
}
